import { Injectable } from '@angular/core';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { HttpClient } from '@angular/common/http';
import { DateService } from '../date/date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { PtLuxonDate } from '../../models/projects/date.models';
import {
  DailyLegLoad,
  DailyLegLoadExtensionFilters,
  DailyLegLoadExtensionModel,
  DailyLegLoadStatus
} from '../../models/daily-leg-loads/daily-leg-load.models';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { AppConfig } from '../../app/app.config';

@Injectable()
export class DailyLegLoadService {
  private readonly legLoadLogTable: DbTable;

  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateService: DateService,
    private dateParserService: DateParserService,
    private timeZoneService: TimeZoneService
  ) {
    this.legLoadLogTable = this.dbContext.tables.find(t => t.name === 'LegLoadLogData');
  }

  // gets latest leg load data and stores it in local storage - created so we can update this data only when loading the leg load log,
  // as before it was refreshing the entire app data
  async updateLegLoadLogDataForDate(date: PtLuxonDate) {
    const dateWithOffset = this.dateParserService.removeOffsetFromLuxonDate(date.dateTime);
    const dateLong = dateWithOffset.toMillis();

    const url = `${this.config.apiUrl}api/dailylegload/getlegloadlogdata/${dateLong}`;
    const data = await this.http.get<DailyLegLoad[]>(url).toPromise();

    // note we dont need to deserialize the dates here
    await this.deleteAllLegLoadLogs();

    if (data) {
      this.saveLegLoadMultipleRecords(data);
    }
  }

  async getLegLoadLogDataForDay(date: PtLuxonDate): Promise<DailyLegLoad[]> {
    let logs = await this.dbContext.getAll(this.legLoadLogTable) as DailyLegLoad[];

    logs = await this.deserializeLegLoadData(logs);

    let filteredData: DailyLegLoad[] = [];

    for (let i = 0; i < logs.length; i++) {
      const sameDay = this.dateService
        .compareDayOffsetsPreApplied(date.dateTime, logs[i].date);
      if (sameDay) {
        filteredData.push(logs[i]);
      }
    }

    filteredData.sort(function (a, b) {
      return a.date.toMillis() - b.date.toMillis();
    });

    if (filteredData) {
      return filteredData;
    }

    filteredData = [];

    return filteredData;
  }

  deleteAllLegLoadLogs() {
    return this.dbContext.removeAll(this.legLoadLogTable);
  }

  saveLegLoadMultipleRecords(logs: DailyLegLoad[]) {
    logs.forEach(record => {
      this.dbContext.addOrUpdate(this.legLoadLogTable, record.id || record.localId, record);
    });
  }

  async getNewLegLoadLocalId() {
    return await this.dbContext.getTempId(this.legLoadLogTable) as number;
  }

  async setStatuses(dailyLegLoadData: DailyLegLoad[]): Promise<DailyLegLoad[]> {
    dailyLegLoadData = this.dateParserService.removeOffsetFromLuxonDates(dailyLegLoadData);

    const url = `${this.config.apiUrl}api/dailylegload/getupdatedstatuses`;
    const data = await this.http
      .post<DailyLegLoad[]>(url, dailyLegLoadData)
      .toPromise();

    return await this.deserializeLegLoadData(data);
  }

  async getTotalLegExtensionData(filters: DailyLegLoadExtensionFilters): Promise<DailyLegLoadExtensionModel> {
    filters.date = this.dateParserService.removeOffsetFromLuxonDate(filters.date);

    const url = `${this.config.apiUrl}api/dailylegload/getlegextensiondata`;

    const result = await this.http.post<DailyLegLoadExtensionModel>(url, filters).toPromise();

    if (result) {
      result.date = await this.dateParserService.deserializeDate(result.date, true, this.timeZoneService);
    }

    return result;
  }

  async quickSaveLegLoadRecords(dailyLegLoads: DailyLegLoad[]) {
    if (!dailyLegLoads || dailyLegLoads.length === 0) {
      return;
    }

    for (let i = 0; i < dailyLegLoads.length; i++) {
      dailyLegLoads[i].date = this.dateParserService.removeOffsetFromLuxonDate(dailyLegLoads[i].date);
    }

    const url = `${this.config.apiUrl}api/dailylegload/quicksaverecords`;
    const data = await this.http
      .post<DailyLegLoad[]>(url, dailyLegLoads)
      .toPromise();

    return await this.deserializeLegLoadData(data);
  }


  getUpdatedStatuses(dailyLegLoads: DailyLegLoad[]): DailyLegLoad[] {
    dailyLegLoads.forEach(legLoad => {
      legLoad.status = this.setLegLoadStatus(legLoad);
      legLoad.statusId = legLoad.status;
    });

    return dailyLegLoads;
  }

  private setLegLoadStatus(editor: DailyLegLoad): DailyLegLoadStatus {
    let count = 0;

    const legs = editor.legs || [];
    const anyLegs = legs.length > 0;
    const missingCount = legs.filter(l => l.load == null).length;
    const presentCount = legs.filter(l => l.load != null).length;

    count += presentCount;

    if (editor.port != null) { count++; }
    if (editor.fore != null) { count++; }
    if (editor.comment && editor.comment.trim()) { count++; }
    if (editor.calculatedElevatedWeight != null) { count++; }
    if (editor.measuredElevatedWeight != null) { count++; }

    if (count === 0) { return DailyLegLoadStatus.NotComplete; }

    // Mirror old v1 behaviour: legs were always "expected", so if none exist treat as in-progress
    if (!anyLegs) { return DailyLegLoadStatus.InProgress; }

    if (missingCount > 0) { return DailyLegLoadStatus.InProgress; }

    return DailyLegLoadStatus.Complete;
  }

  async deserializeLegLoadData(legLoadData: DailyLegLoad[]): Promise<DailyLegLoad[]> {
    if (!legLoadData) {
      return null;
    }

    for (let i = 0; i < legLoadData.length; i++) {
      legLoadData[i].date = await this.dateParserService.deserializeDate(legLoadData[i].date, true, this.timeZoneService);
    }

    return legLoadData;
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-leg-loads/daily-leg-load.service.ts